import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Device } from '@twilio/voice-sdk';
import api from '@/lib/api';

const VoiceContext = createContext(null);

export function VoiceProvider({ children }) {
  const [device, setDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  // idle | connecting | ringing | in-progress
  const [callStatus, setCallStatus] = useState('idle');
  const [isDeviceReady, setIsDeviceReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callerInfo, setCallerInfo] = useState(null); // { number, name }
  // { number, name, autoCall, ts } — consumed by the floating Dialer to prefill/start a call
  const [dialRequest, setDialRequest] = useState(null);
  const deviceRef = useRef(null);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    setCallDuration(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCallDuration(d => d + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCallDuration(0);
  }, []);

  const attachCallEvents = useCallback((call, info = null) => {
    if (info) setCallerInfo(info);

    call.on('ringing', () => setCallStatus('ringing'));

    call.on('accept', () => {
      setCallStatus('in-progress');
      startTimer();
    });

    call.on('disconnect', () => {
      setActiveCall(null);
      setCallStatus('idle');
      setIsMuted(false);
      setCallerInfo(null);
      stopTimer();
    });

    call.on('cancel', () => {
      setActiveCall(null);
      setCallStatus('idle');
      setIsMuted(false);
      setCallerInfo(null);
      stopTimer();
    });

    call.on('error', (err) => {
      console.error('Call error:', err.code, err.message);
      setActiveCall(null);
      setCallStatus('idle');
      setIsMuted(false);
      setCallerInfo(null);
      stopTimer();
    });
  }, [startTimer, stopTimer]);

  const refreshToken = useCallback(async (dev) => {
    try {
      const res = await api.get('/voice/token');
      dev.updateToken(res.data.token);
    } catch (err) {
      console.error('Twilio token refresh failed:', err);
    }
  }, []);

  const initDevice = useCallback(async () => {
    const bwToken = localStorage.getItem('bw_token');
    if (!bwToken) return;

    try {
      const res = await api.get('/voice/token');
      const { token } = res.data;

      const dev = new Device(token, {
        logLevel: 'warn',
        codecPreferences: ['opus', 'pcmu'],
      });

      dev.on('registered', () => setIsDeviceReady(true));
      dev.on('unregistered', () => setIsDeviceReady(false));

      dev.on('error', (error) => {
        console.error('Twilio Device error:', error.code, error.message);
        // Token expired or invalid
        if ([31205, 31208, 20104].includes(error.code)) {
          refreshToken(dev);
        }
      });

      dev.on('tokenWillExpire', () => refreshToken(dev));

      dev.on('incoming', (call) => {
        const from = call.parameters?.From || 'Unknown';
        setIncomingCall(call);
        setCallerInfo({ number: from, name: from });

        call.on('cancel', () => {
          setIncomingCall(null);
          setCallerInfo(null);
        });
      });

      await dev.register();
      deviceRef.current = dev;
      setDevice(dev);
    } catch (err) {
      // Twilio not configured yet — silently skip
      if (err?.response?.status !== 503) {
        console.error('Failed to init Twilio Device:', err);
      }
    }
  }, [refreshToken]);

  useEffect(() => {
    initDevice();
    return () => {
      stopTimer();
      if (deviceRef.current) {
        deviceRef.current.destroy();
        deviceRef.current = null;
      }
    };
  }, []);

  const makeCall = useCallback(async (toNumber, displayName = null) => {
    if (!device) throw new Error('Voice device not ready');
    setCallStatus('connecting');
    setIsMuted(false);
    setCallerInfo({ number: toNumber, name: displayName || toNumber });

    const call = await device.connect({ params: { To: toNumber } });
    setActiveCall(call);
    attachCallEvents(call, { number: toNumber, name: displayName || toNumber });
    return call;
  }, [device, attachCallEvents]);

  const hangUp = useCallback(() => {
    if (activeCall) activeCall.disconnect();
    if (incomingCall) incomingCall.reject();
    setActiveCall(null);
    setIncomingCall(null);
    setCallStatus('idle');
    setIsMuted(false);
    setCallerInfo(null);
    stopTimer();
  }, [activeCall, incomingCall, stopTimer]);

  const acceptIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.accept();
    setActiveCall(incomingCall);
    setIncomingCall(null);
    setCallStatus('in-progress');
    attachCallEvents(incomingCall, callerInfo);
    startTimer();
  }, [incomingCall, callerInfo, attachCallEvents, startTimer]);

  const rejectIncoming = useCallback(() => {
    if (!incomingCall) return;
    incomingCall.reject();
    setIncomingCall(null);
    setCallerInfo(null);
  }, [incomingCall]);

  const toggleMute = useCallback(() => {
    if (!activeCall) return;
    const next = !isMuted;
    activeCall.mute(next);
    setIsMuted(next);
  }, [activeCall, isMuted]);

  const sendDigits = useCallback((digit) => {
    if (activeCall) activeCall.sendDigits(digit);
  }, [activeCall]);

  const requestDial = useCallback((number, name = null, autoCall = true) => {
    setDialRequest({ number, name, autoCall, ts: Date.now() });
  }, []);

  const clearDialRequest = useCallback(() => {
    setDialRequest(null);
  }, []);

  return (
    <VoiceContext.Provider value={{
      device,
      activeCall,
      incomingCall,
      callStatus,
      isDeviceReady,
      isMuted,
      callDuration,
      callerInfo,
      makeCall,
      hangUp,
      acceptIncoming,
      rejectIncoming,
      toggleMute,
      sendDigits,
      dialRequest,
      requestDial,
      clearDialRequest,
    }}>
      {children}
    </VoiceContext.Provider>
  );
}

export function useVoice() {
  const ctx = useContext(VoiceContext);
  if (!ctx) throw new Error('useVoice must be used within VoiceProvider');
  return ctx;
}
